import { useState } from "react";
import axios from "axios";
import { CheckCircle, KeyRound, LockKeyhole, ShieldCheck } from "lucide-react";

// Staff (team) self-service password reset. Same look as the team Login
// screen in App.js (login-page / login-card), and the same two-step shape
// as the client portal's version (ClientPortal.js "Forgot password?" +
// ClientResetPassword.js):
//   /forgot-password  -> POST /api/auth/forgot-password
//   /reset-password   -> POST /api/auth/reset-password (link from the email)
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENERIC_MESSAGE = "If an account exists for this email, a password reset link has been sent.";

// Mirrors PasswordSecurityService.validate_password_strength (backend) so
// the user sees the problem before submitting; the backend still enforces it.
const SPECIAL_CHARS = "!@#$%^&*()_+-=[]{}|;:,.<>?";
function passwordPolicyError(password) {
  if (password.length < 8) return "Password must be at least 8 characters long";
  const ok = /[A-Z]/.test(password) && /[a-z]/.test(password) && /\d/.test(password)
    && [...password].some((c) => SPECIAL_CHARS.includes(c));
  return ok ? "" : "Password must contain uppercase, lowercase, digit, and special character";
}

function StaffAuthShell({ eyebrow, title, copy, children }) {
  return (
    <main className="login-page dark noise-overlay">
      <section className="login-brand-panel">
        <div className="brand-mark"><ShieldCheck size={32} /></div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="login-copy">{copy}</p>
      </section>
      {children}
    </main>
  );
}

const backToLogin = (
  <p style={{ marginTop: 18, fontSize: 14 }}>
    <a href="/" style={{ color: "var(--gold)" }} data-testid="staff-reset-back-to-login">Back to sign in</a>
  </p>
);

export function StaffForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setMessage(res.data?.message || GENERIC_MESSAGE);
    } catch (err) {
      setError(err.response?.status === 429
        ? "Too many requests. Please wait a while and try again."
        : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StaffAuthShell
      eyebrow="Secure Access"
      title="Forgot your password?"
      copy="Enter the email you use to sign in to MedSpaBookings OS and we'll send you a link to choose a new password."
    >
      <section className="login-card glass-panel" data-testid="staff-forgot-password-panel">
        <div className="login-card-header"><KeyRound size={22} /><div><h2>Reset Password</h2><p>The link expires after 30 minutes.</p></div></div>
        {message ? (
          <div data-testid="staff-forgot-password-message">
            <p style={{ display: "flex", gap: 10, alignItems: "flex-start" }}><CheckCircle size={20} style={{ color: "var(--gold)", flexShrink: 0 }} />{message}</p>
            <p className="muted" style={{ fontSize: 14 }}>Didn't get it? Check your spam folder, or contact your Master Administrator.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="login-form">
            <label>Email<input data-testid="staff-forgot-password-email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
            {error && <div className="form-error" data-testid="staff-forgot-password-error">{error}</div>}
            <button className="primary-btn" data-testid="staff-forgot-password-submit" type="submit" disabled={loading}>{loading ? "Sending..." : "Send Reset Link"}</button>
          </form>
        )}
        {backToLogin}
      </section>
    </StaffAuthShell>
  );
}

export function StaffResetPassword() {
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    const policyError = passwordPolicyError(password);
    if (policyError) {
      setError(policyError);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      // Any stale session in this browser was revoked server-side.
      localStorage.removeItem("msb_token");
      localStorage.removeItem("msb_user");
      localStorage.removeItem("msb_permissions");
      setDone(true);
    } catch (err) {
      setError(err.response?.status === 429
        ? "Too many attempts. Please wait a while and try again."
        : err.response?.data?.detail || "Password reset failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  let body;
  if (!token) {
    body = (
      <div data-testid="staff-reset-password-missing-token">
        <div className="form-error">This reset link is incomplete. Please request a new one.</div>
        <p style={{ marginTop: 18, fontSize: 14 }}><a href="/forgot-password" style={{ color: "var(--gold)" }}>Request a new reset link</a></p>
      </div>
    );
  } else if (done) {
    body = (
      <div data-testid="staff-reset-password-success">
        <p style={{ display: "flex", gap: 10, alignItems: "flex-start" }}><CheckCircle size={20} style={{ color: "var(--gold)", flexShrink: 0 }} />Your password has been reset. All existing sessions were signed out.</p>
        <a className="primary-btn" href="/" data-testid="staff-reset-password-sign-in" style={{ display: "inline-block", textAlign: "center", textDecoration: "none" }}>Sign in</a>
      </div>
    );
  } else {
    body = (
      <form onSubmit={submit} className="login-form">
        <label>New Password<input data-testid="staff-reset-password-input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} /></label>
        <label>Confirm New Password<input data-testid="staff-reset-password-confirm-input" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} /></label>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character.</p>
        {error && (
          <div className="form-error" data-testid="staff-reset-password-error">
            {error}
            {/expired|invalid/i.test(error) && <> <a href="/forgot-password" style={{ color: "var(--gold)" }}>Request a new link</a></>}
          </div>
        )}
        <button className="primary-btn" data-testid="staff-reset-password-submit" type="submit" disabled={loading}>{loading ? "Resetting..." : "Reset Password"}</button>
      </form>
    );
  }

  return (
    <StaffAuthShell
      eyebrow="Secure Access"
      title="Choose a new password"
      copy="If two-factor authentication is enabled on your account, you'll still be asked for your verification code when you sign in."
    >
      <section className="login-card glass-panel" data-testid="staff-reset-password-panel">
        <div className="login-card-header"><LockKeyhole size={22} /><div><h2>New Password</h2><p>Reset links are single-use.</p></div></div>
        {body}
        {backToLogin}
      </section>
    </StaffAuthShell>
  );
}
